import { capitalizeEachWord } from '@utils/StringUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, PageName } from '@utils/pageConfig/Types';
import { FC } from 'react';
import { PgAvailabilityState } from '@state/feature/availability-pg/AvailabilityPgSingleCountrySlice';
import PgAvailability from '../pg-availability/PgAvailability';
import { WithAlert } from '@components/design';
import { feedback } from '@ecosystem/newron-design-system';

interface PgAvailabilityComponentProps
  extends Pick<
    PgAvailabilityState,
    'periodLabel' | 'totalAvailablePgs' | 'totalSubscriptionPgs' | 'unavailablePgs' | 'isMultiCountry'
  > {}

const PgAvailabilityComponent: FC<PgAvailabilityComponentProps> = ({
  periodLabel,
  totalAvailablePgs,
  totalSubscriptionPgs,
  unavailablePgs,
  isMultiCountry,
}) => {
  const { ActionType } = feedback;
  const pagesConfig = usePageConfig();
  const { appliedFilters, pageName, pageType } = pagesConfig;
  const { selectedFilters } = appliedFilters[pageType];

  let selectedPG: string = selectedFilters
    .filter((filter) => filter.filterKey === FilterKeys.PRODUCT_GROUP)
    .flatMap((filter) => filter.options)[0]?.value;

  const checkIsMultiPg: boolean =
    selectedFilters.filter((x) => x.filterKey === FilterKeys.PRODUCT_GROUP)[0]?.options.length > 1;

  let pgNum = unavailablePgs.filter((c) => selectedPG === c);
  let availablePg = pgNum.filter((c) => c !== selectedPG).length;

  const getIsDisplay = (currentPageName) =>
    [
      PageName.RETAILER_OVERVIEW,
      PageName.CATEGORY_LEADERBOARD,
      PageName.EXPLORE,
      PageName.OPPORTUNITY_REVIEW,
      PageName.TRADING_ASSISTANT,
    ].includes(currentPageName);

  const toDisplayText = (): boolean => {
    if (isMultiCountry) {
      return false;
    }

    return getIsDisplay(pageName)
      ? totalAvailablePgs !== totalSubscriptionPgs
      : pgNum.length >= 0 && unavailablePgs.length > 0;
  };

  const getText = (period: string, pgAvailable: number, totalPg: number): string => {
    return `${period}: ${pgAvailable.toString()} out of ${totalPg.toString()} product ${
      pgAvailable > 1 ? 'categories' : 'category'
    } loaded. For more product categories try an earlier time frame. `;
  };

  const text = getIsDisplay(pageName)
    ? getText(periodLabel, totalAvailablePgs, totalSubscriptionPgs)
    : getText(periodLabel, availablePg, pgNum.length);

  /**
   * Returns the different types of warning message based on if its a single pg / no pg is selected.
   *
   * @param selectedPG - The selected PG.
   * @param period - The period.
   * @returns The warning message text.
   */
  const getAvailabilityWarningText = (productGroup: string, period: string): string =>
    productGroup !== undefined && productGroup !== null && !checkIsMultiPg
      ? `${capitalizeEachWord(
          productGroup,
        )} is not yet available for ${period}. Please select an earlier time frame or a different category. `
      : text;

  return toDisplayText() ? (
    <div className=" bg-white">
      <span data-testid="pg-availability-warning-banner">
        <WithAlert
          title={
            <PgAvailability
              text={getAvailabilityWarningText(selectedPG, periodLabel)}
              periodValue={periodLabel}
              unavailablePgs={unavailablePgs}
            />
          }
          type="warn"
          actionType={ActionType.DEFAULT}
        />
      </span>
    </div>
  ) : null;
};

export default PgAvailabilityComponent;
